import { produce } from 'immer'
import { AnyAction } from '@reduxjs/toolkit'
import {
  GET_TASKS,
  GET_TASKS_SUCCESS,
  GET_TASKS_FAILED,
  GET_TASK_SUMMARY_SUCCESS,
  GET_TASK_SUMMARY_FAILED,
} from '../constants'

export interface IResponseStatus {
  message: string
  error: string
  data: any
  status: number
}

export interface IResolvedReference {
  label: string
  link: string | null
}

export interface ITask {
  id: string
  createdAt: string
  name: string
  description: string
  isOpen: boolean
  priority: 'URGENT' | 'NORMAL'
  referenceType: string | null
  reference: string | null
  companyId: string
  companyName: string | null
  userRoleId: string
  currentStatus: 'TODO' | 'DONE' | 'REJECTED' | null
  resolvedReference: IResolvedReference
  /**
   * Revalidation calculée par l'API : `false` signifie que la cause a disparu
   * depuis l'émission, `null` qu'aucun prédicat ne couvre ce type de tâche.
   * Ne jamais lire `null` comme « levée ».
   */
  causeStillHolds: boolean | null
  /**
   * Pourquoi la cause a cessé, quand `causeStillHolds` vaut `false`.
   *
   *   RESOLVED      le problème a disparu des données
   *   OUT_OF_SCOPE  le problème est intact, mais on a décidé de ne plus le
   *                 signaler — une référence de prothèse rejetée parce qu'elle
   *                 n'est pas remboursée
   *
   * Les deux ne se disent pas de la même façon : annoncer « le problème
   * n'existe plus » sur une référence refusée serait faux. L'API calcule la
   * distinction pour que l'écran n'ait pas à comparer `name` à un libellé
   * français, qui dériverait sans bruit le jour où il change.
   */
  causeLiftedReason: 'RESOLVED' | 'OUT_OF_SCOPE' | null
  /**
   * Rejeter cette tâche éteint-il le signalement pour de bon ?
   *
   * `false` pour presque tout : fermer sans corriger la donnée fait revenir une
   * tâche identique au cycle suivant. `true` pour « Référence implantée sans
   * tarif », dont le rejet déclare la référence hors périmètre de facturation,
   * dans tous les centres. À dire avant le clic, pas après.
   */
  rejectionEndsSignal: boolean
  ageInDays: number
  commentCount: number
}

export interface ITaskSummary {
  open: number
  urgent: number
  causeLifted: number
  olderThan90Days: number
  byName: { name: string; count: number }[]
}

export interface IStateTasks {
  tasks: ITask[]
  totalPages: number
  summary: ITaskSummary
  loading: boolean
  error: string | null
}

const initialState: IStateTasks = {
  tasks: [],
  totalPages: 0,
  summary: {
    open: 0,
    urgent: 0,
    causeLifted: 0,
    olderThan90Days: 0,
    byName: [],
  },
  loading: false,
  error: null,
}

const tasksReducer = (state = initialState, action: AnyAction): IStateTasks =>
  produce(state, (draft) => {
    switch (action.type) {
      case GET_TASKS:
        draft.loading = true
        draft.error = null
        break
      // `parseResponse` place la réponse entière dans `payload` — corps et
      // statut. Le corps paginé est donc en `payload.data`, et la liste en
      // `payload.data.data`, comme dans le module alerts.
      case GET_TASKS_SUCCESS:
        draft.tasks = action.payload?.data?.data ?? []
        draft.totalPages = action.payload?.data?.totalPages ?? 0
        draft.loading = false
        break
      case GET_TASKS_FAILED:
        draft.loading = false
        draft.error = action.error ?? 'erreur inconnue'
        break
      case GET_TASK_SUMMARY_SUCCESS:
        draft.summary = action.payload?.data ?? initialState.summary
        break
      case GET_TASK_SUMMARY_FAILED:
        draft.error = action.error ?? 'erreur inconnue'
        break
      default:
        break
    }
  })

export default tasksReducer
